import React, { useCallback, useMemo, useState } from "react";
import classnames from "classnames";
import { Collapse } from "@blueprintjs/core";
import { Caret } from "modules/common/components/collapse/Caret";
import { CollapseExpandAll } from "modules/common/components/table/CollapseExpandAll";
import { Search } from "modules/common/components/form/Search";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { PlanrTag } from "modules/common/components/planr/tag/PlanrTag";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { RoleAccessRecordSnapshotType } from "../../models/access-store";
import {
    AccessItem,
    AccessModuleData,
    AccessState,
    accessState,
    isEnabled,
    isGodMode,
    matches,
    splitSection,
    withHint,
} from "../../models/access-tree";
import { AccessCheck } from "./AccessCheck";
import { SectionName } from "./SectionName";
import styles from "./AccessEditor.module.scss";

type Filter = "all" | "on" | "diff";

const STATE_TEXT: Record<AccessState, string> = {
    on: "включено",
    mgr: "включено как руководителю подразделения",
    plus: "разрешено лично",
    minus: "запрещено лично",
    off: "выключено",
};

const readCollapsed = (key: string): string[] => {
    try {
        return JSON.parse(localStorage.getItem(key) ?? "[]");
    } catch {
        return [];
    }
};

const writeCollapsed = (key: string, value: string[]) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // свёрнутые блоки — удобство, без хранилища просто не запоминаются
    }
};

/**
 * Права должности или сотрудника: модули — сворачиваемые блоки, внутри таблица
 * «раздел × Чтение / Запись / Дополнительно». В карточке сотрудника (`personal`) видно,
 * откуда право: должность, руководство подразделением или личное исключение.
 */
export const AccessEditor = ({
    modules,
    access,
    onToggle,
    personal = false,
    readonly,
    storageKey,
    onModuleChange,
    admin,
}: AccessEditorProps) => {
    const collapsedKey = `rost.access.collapsed.${storageKey}`;
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState<Filter>("all");
    const [collapsed, setCollapsed] = useState<string[]>(() => readCollapsed(collapsedKey));

    const records = useMemo(() => new Map(access.map((a) => [a.name, a])), [access]);
    const god = personal && isGodMode(access);
    const locked = readonly || god;

    const stateOf = useCallback((code: string) => accessState(records.get(code), personal), [records, personal]);

    const labels = useMemo(() => {
        const map = new Map<string, string>();
        modules.forEach((m) =>
            m.sections.forEach((s) =>
                s.items.forEach((i) => map.set(i.code, `${m.name} → ${s.name}${i.action ? ` · ${i.action}` : ""}`))
            )
        );
        return map;
    }, [modules]);

    const toggleModule = useCallback(
        (name: string) =>
            setCollapsed((prev) => {
                const next = prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name];
                writeCollapsed(collapsedKey, next);
                return next;
            }),
        [collapsedKey]
    );

    const collapseAll = useCallback(
        (value: boolean) => {
            const next = value ? modules.map((m) => m.name) : [];
            writeCollapsed(collapsedKey, next);
            setCollapsed(next);
        },
        [modules, collapsedKey]
    );

    const toggle = useCallback(
        (code: string) => {
            if (!locked) {
                onToggle(code, !isEnabled(stateOf(code)));
            }
        },
        [locked, onToggle, stateOf]
    );

    // вернуть как в должности: значение должности снимает исключение (см. SetEmployerFunctionality)
    const revert = useCallback(
        (code: string) => {
            const record = records.get(code);
            if (!locked && record) {
                onToggle(code, record.roleValue);
            }
        },
        [locked, onToggle, records]
    );

    const visible = useCallback(
        (moduleName: string, sectionName: string, item: AccessItem) => {
            if (!matches(query, moduleName, sectionName, item.action)) return false;
            if (filter === "on") return isEnabled(stateOf(item.code));
            if (filter === "diff") return !!records.get(item.code)?.overwritten;
            return true;
        },
        [query, filter, stateOf, records]
    );

    const exceptions = personal ? access.filter((a) => a.overwritten && labels.has(a.name)) : [];
    const total = modules.reduce((sum, m) => sum + m.codes.length, 0);
    const totalOn = modules.reduce((sum, m) => sum + m.codes.filter((c) => isEnabled(stateOf(c))).length, 0);
    const fromManager = personal ? access.filter((a) => accessState(a, true) === "mgr").length : 0;
    const searching = !!query.trim() || filter !== "all";

    const renderCheck = (item: AccessItem, sectionName: string, chip: boolean) => {
        const state = stateOf(item.code);
        const label = withHint(`${sectionName}${item.action ? ` · ${item.action}` : ""}: ${STATE_TEXT[state]}`, item.code);
        const record = records.get(item.code);

        return (
            <span className={styles.cell} key={item.code}>
                <AccessCheck state={state} label={label} disabled={locked} onClick={() => toggle(item.code)}>
                    {chip ? item.action : undefined}
                </AccessCheck>
                {personal && state === "plus" && (
                    <span title="Разрешено лично: должность это право не даёт">
                        <PlanrTag type="success">+ лично</PlanrTag>
                    </span>
                )}
                {personal && state === "minus" && (
                    <span title="Запрещено лично: должность это право даёт">
                        <PlanrTag type="danger">− лично</PlanrTag>
                    </span>
                )}
                {personal && state === "mgr" && (
                    <span title={record?.reason ?? undefined}>
                        <PlanrTag type="neutral">руководитель</PlanrTag>
                    </span>
                )}
                {personal && !locked && (state === "plus" || state === "minus") && (
                    <PlanrButton
                        type="neutral"
                        size="small"
                        icon="general-undo"
                        round={true}
                        className={styles.revert}
                        title={`Вернуть как в должности (${record?.roleValue ? "вкл" : "выкл"})`}
                        onClick={() => revert(item.code)}
                    />
                )}
            </span>
        );
    };

    // при полном доступе отдельные права ничего не решают — вместо них одна плашка
    if (god) {
        return (
            <div className={styles.editor}>
                <div className={styles.godBanner}>
                    <GeneralIcon type="general-checkbox-dark" size={20} />
                    <div>
                        <div>
                            {admin
                                ? "Администратор — все права включены"
                                : "Полный доступ по должности — все права включены"}
                        </div>
                        <div className={styles.muted}>
                            {admin
                                ? "Личные исключения сохранены и снова начнут действовать, если снять роль администратора."
                                : "Личные исключения не действуют."}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.editor}>
            {personal && (
                <div className={styles.summary}>
                    <div className={styles.summaryMain}>
                        {
                            <>
                                <div className={styles.muted}>
                                    Включено {totalOn} из {total} · исключений {exceptions.length}
                                    {exceptions.length > 0 &&
                                        `: разрешено ${exceptions.filter((e) => e.value).length}, запрещено ${
                                            exceptions.filter((e) => !e.value).length
                                        }`}
                                    {fromManager > 0 && ` · как руководителю ${fromManager}`}
                                </div>
                                {exceptions.length > 0 && (
                                    <div className={styles.exceptions}>
                                        {exceptions.map((e) => (
                                            <span className={styles.exception} key={e.name}>
                                                <PlanrTag type={e.value ? "success" : "danger"}>
                                                    {e.value ? "+" : "−"}
                                                </PlanrTag>
                                                {labels.get(e.name)}
                                                {!locked && (
                                                    <PlanrButton
                                                        type="neutral"
                                                        size="small"
                                                        icon="general-undo"
                                                        round={true}
                                                        className={styles.revert}
                                                        title="Вернуть как в должности"
                                                        onClick={() => revert(e.name)}
                                                    />
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </>
                        }
                    </div>
                    <div className={styles.legend}>
                        <span title="Право даёт должность сотрудника">
                            <AccessCheck state="on" label="по должности" disabled={true} size={14} />
                            по должности
                        </span>
                        <span title="Право даётся руководителю подразделения в структуре организации, должность его не даёт">
                            <AccessCheck state="mgr" label="как руководителю" disabled={true} size={14} />
                            как руководителю подразделения
                        </span>
                        <span title="Должность право не даёт, но сотруднику оно разрешено лично">
                            <AccessCheck state="plus" label="разрешено лично" disabled={true} size={14} />
                            разрешено лично
                        </span>
                        <span title="Должность право даёт, но сотруднику оно запрещено лично. Клик по галочке ставит или снимает запрет">
                            <AccessCheck state="minus" label="запрещено лично" disabled={true} size={14} />
                            запрещено лично
                        </span>
                    </div>
                </div>
            )}

            <div className={styles.toolbar}>
                <Search query={query} onSearch={setQuery} style={{ marginLeft: 0 }} />
                <div className={styles.filters}>
                    <button
                        type="button"
                        className={classnames({ [styles.active]: filter === "all" })}
                        onClick={() => setFilter("all")}
                        title="Показать все права"
                    >
                        Все
                    </button>
                    <button
                        type="button"
                        className={classnames({ [styles.active]: filter === "on" })}
                        onClick={() => setFilter("on")}
                        title="Показать только включённые права"
                    >
                        Включённые
                    </button>
                    {personal && (
                        <button
                            type="button"
                            className={classnames({ [styles.active]: filter === "diff" })}
                            onClick={() => setFilter("diff")}
                            title="Показать только права, изменённые лично для этого сотрудника"
                        >
                            Отличия от должности
                        </button>
                    )}
                </div>
                <span className={styles.spacer} />
                {!personal && (
                    <span className={styles.muted}>
                        Включено {totalOn} из {total}
                    </span>
                )}
                <CollapseExpandAll onChange={collapseAll} />
            </div>

            <div className={styles.modules}>
                {modules.map((module) => {
                    const sections = module.sections
                        .map((s) => ({ name: s.name, items: s.items.filter((i) => visible(module.name, s.name, i)) }))
                        .filter((s) => s.items.length > 0);

                    if (searching && sections.length === 0) {
                        return null;
                    }

                    const isCollapsed = !searching && collapsed.includes(module.name);
                    const on = module.codes.filter((c) => isEnabled(stateOf(c))).length;

                    return (
                        <div className={styles.module} key={module.name}>
                            <h1
                                className={`planr-block-header collapser ${styles.moduleHeader}`}
                                onClick={() => toggleModule(module.name)}
                            >
                                <span className={styles.moduleName}>{module.name}</span>
                                <span
                                    className={styles.bar}
                                    title={`Включено ${on} из ${module.codes.length} прав модуля`}
                                >
                                    <i style={{ width: `${Math.round((on / module.codes.length) * 100)}%` }} />
                                </span>
                                <span className={styles.muted}>
                                    {on} из {module.codes.length}
                                </span>
                                {onModuleChange && !locked && (
                                    <span className={styles.moduleActions} onClick={(e) => e.stopPropagation()}>
                                        <PlanrButton
                                            type="neutral"
                                            size="small"
                                            disabled={on === module.codes.length}
                                            onClick={() => onModuleChange(module.codes, true)}
                                            title={`Включить все права модуля «${module.name}»`}
                                        >
                                            Выбрать все
                                        </PlanrButton>
                                        <PlanrButton
                                            type="neutral"
                                            size="small"
                                            disabled={on === 0}
                                            onClick={() => onModuleChange(module.codes, false)}
                                            title={`Выключить все права модуля «${module.name}»`}
                                        >
                                            Снять все
                                        </PlanrButton>
                                    </span>
                                )}
                                <Caret collapsed={isCollapsed} />
                            </h1>
                            <Collapse isOpen={!isCollapsed} keepChildrenMounted={false}>
                                <div className={`collapse ${styles.moduleBody}`}>
                                    <table className={styles.table}>
                                        <colgroup>
                                            <col className={styles.colSection} />
                                            <col className={styles.colCheck} />
                                            <col className={styles.colCheck} />
                                            <col />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>Раздел</th>
                                                <th className={styles.center}>Чтение / доступ</th>
                                                <th className={styles.center}>Запись</th>
                                                <th>Дополнительно</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sections.map((section) => {
                                                const { read, edit, rest } = splitSection(section.items);

                                                return (
                                                    <tr key={section.name}>
                                                        <td>
                                                            <SectionName name={section.name} items={section.items} />
                                                        </td>
                                                        <td className={styles.center}>
                                                            {read && renderCheck(read, section.name, false)}
                                                        </td>
                                                        <td className={styles.center}>
                                                            {edit && renderCheck(edit, section.name, false)}
                                                        </td>
                                                        <td>
                                                            <div className={styles.chips}>
                                                                {rest.map((i) => renderCheck(i, section.name, true))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </Collapse>
                        </div>
                    );
                })}
                {searching &&
                    !modules.some((m) => m.sections.some((s) => s.items.some((i) => visible(m.name, s.name, i)))) && (
                        <div className={styles.empty}>Ничего не найдено. Измените запрос или фильтр.</div>
                    )}
            </div>
        </div>
    );
};

interface AccessEditorProps {
    /** Сотрудник — администратор (GodMode): иначе полный доступ дан должностью */
    admin?: boolean;
    /** Включить / снять все права модуля разом — только у должности (замена набора прав целиком) */
    onModuleChange?: (codes: string[], state: boolean) => void;
    modules: AccessModuleData[];
    access: RoleAccessRecordSnapshotType[];
    onToggle: (functionality: string, state: boolean) => void;
    /** Карточка сотрудника или аутсорсера: показывать источник права и личные исключения */
    personal?: boolean;
    readonly?: boolean;
    /** Ключ для запоминания свёрнутых модулей: «role», «employee», «outsourcer» */
    storageKey: string;
}
